import React, { useState } from 'react';
import {
  Box, Button, Heading, Text, VStack,
  SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Alert, AlertIcon, Card, CardBody, Divider
} from '@chakra-ui/react';

const Capriole = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const launchAndCollectData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!window.electronAPI) {
        setError('Electron API not found. Are you running in Electron?');
        return;
      }

      // Step 1: Open Capriole browser (this will auto-login)
      console.log('Opening Capriole browser...');
      window.electronAPI.openCapriole();

      // Step 2: Wait for login and auth parameter capture (give it time to load, authenticate, and capture params)
      await new Promise(resolve => setTimeout(resolve, 15000)); // 15 second delay

      // Step 3: Run the Chrysalis strategy analysis
      console.log('Collecting Chrysalis strategy metrics...');
      const result = await window.electronAPI.runChrysalis();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="start">
        <Heading>Chrysalis Strategy Dashboard</Heading>
        <Text>
          Click the button below to automatically:
          <br />
          1. Launch the Capriole browser
          <br />
          2. Log in with your credentials
          <br />
          3. Collect and analyze Chrysalis strategy metrics
        </Text>

        <Button
          colorScheme="purple"
          size="lg"
          onClick={launchAndCollectData}
          isLoading={loading}
          loadingText="Collecting data..."
        >
          Launch Capriole & Collect Data
        </Button>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {data && (
          <VStack w="full" align="stretch" spacing={6}>
            <Box p={4} borderWidth={1} borderRadius="lg" bg={data.score.signal === 'Buy/Hold' ? 'green.50' : 'orange.50'}>
              <Heading size="md" mb={2}>Chrysalis Score: {data.score.total}/6</Heading>
              <Heading size="lg" color={data.score.signal === 'Buy/Hold' ? 'green.600' : 'orange.600'}>
                {data.score.signal}
              </Heading>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <MetricCard
                label="Macro Index"
                value={(data.metrics.macro?.oscillator || 0).toFixed(2)}
                help={`Score: ${data.score.breakdown.macro}`}
              />
              <MetricCard label="Spectrum" value={data.metrics.spectrum?.weight_target > 0 ? "Bullish" : "Bearish"} help={`Score: ${data.score.breakdown.spectrum}`} />
              <MetricCard label="Node" value={data.metrics.node?.weight_target > 0 ? "Bullish" : "Bearish"} help={`Score: ${data.score.breakdown.node}`} />
              <MetricCard label="Open Interest" value={data.metrics.oi} help={`Score: ${data.score.breakdown.oi}`} />
              <MetricCard label="Valuation Models" value={data.metrics.models?.mvrv_z < 0.5 ? "Undervalued" : "Neutral/Over"} help={`Score: ${data.score.breakdown.models}`} />
              <MetricCard label="LTH Inflation" value={data.metrics.lth < 0.5 ? "Low" : "High"} help={`Score: ${data.score.breakdown.lth}`} />
            </SimpleGrid>

            <Divider />

            <Card>
              <CardBody>
                <Heading size="sm" mb={4}>Projections</Heading>
                <Stat>
                  <StatLabel>Projected 1Y Return</StatLabel>
                  <StatNumber>+{(data.backtest.cagr * 100).toFixed(0)}%</StatNumber>
                  <StatHelpText>Based on historical performance</StatHelpText>
                </Stat>
                <Text mt={2} fontSize="sm" color="gray.600">
                  Current Price: ${data.metrics.price?.toLocaleString()}
                </Text>
              </CardBody>
            </Card>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

const MetricCard = ({ label, value, help }: { label: string, value: string, help: string }) => (
  <Card>
    <CardBody>
      <Stat>
        <StatLabel>{label}</StatLabel>
        <StatNumber fontSize="xl">{value}</StatNumber>
        <StatHelpText>{help}</StatHelpText>
      </Stat>
    </CardBody>
  </Card>
);



export default Capriole;
